import React, { useState, useEffect } from 'react';
import './AdminMembers.css';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import GroupSelectionModal from './GroupSelectionModal';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { membersAPI } from '../../services/api';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FaCamera, FaUpload } from 'react-icons/fa';

function AdminMembers() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [upImg, setUpImg] = useState(null);
    const [crop, setCrop] = useState({
        unit: 'px',
        x: 0,
        y: 0,
        width: 0,
        height: 0
    });
    const [showCropper, setShowCropper] = useState(false);
    const [imgRef, setImgRef] = useState(null);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [selectedMemberId, setSelectedMemberId] = useState(null);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [memberGroups, setMemberGroups] = useState({});
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        position: '',
        department: '',
        bio: '',
        picture: null,
        picturePreview: null
    });

    const storage = getStorage();

    // Fetch members and their groups on component mount
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Fetch members and groups in parallel
            const [membersResponse, groupsResponse] = await Promise.all([
                membersAPI.getAllMembers(),
                membersAPI.getAllMemberGroups()
            ]);
                        
            // The groupsResponse is now already a map with memberId as keys,
            // so we don't need to transform it anymore
            
            const normalizedMembers = Array.isArray(membersResponse)
                ? membersResponse
                : Array.isArray(membersResponse?.members)
                    ? membersResponse.members
                    : [];

            const normalizedMemberGroups =
                groupsResponse && typeof groupsResponse === 'object' && !Array.isArray(groupsResponse)
                    ? groupsResponse
                    : {};

            setMembers(normalizedMembers);
            setMemberGroups(normalizedMemberGroups);
            
        } catch (err) {
            setError('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('social.')) {
            const socialField = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                social: {
                    ...prev.social,
                    [socialField]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const onImageLoad = (image) => {
        setImgRef(image);
        
        // Set initial crop to center square
        const width = Math.min(image.width, image.height);
        const height = width;
        const x = (image.width - width) / 2;
        const y = (image.height - height) / 2;
        
        setCrop({
            unit: 'px',
            x,
            y,
            width,
            height
        });
    };

    const handleCropComplete = async () => {
        if (!imgRef || !crop.width || !crop.height) {
          return;
        }
      
        const canvas = document.createElement('canvas');
        const scaleX = imgRef.naturalWidth / imgRef.width;
        const scaleY = imgRef.naturalHeight / imgRef.height;
      
        canvas.width = Math.floor(crop.width * scaleX);
        canvas.height = Math.floor(crop.height * scaleY);
      
        const ctx = canvas.getContext('2d');
      
        ctx.drawImage(
          imgRef,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          canvas.width,
          canvas.height
        );
      
        canvas.toBlob((blob) => {
          if (!blob) {
            return;
          }
      
          const croppedFile = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
          const previewUrl = URL.createObjectURL(croppedFile);
      
          setFormData(prev => ({
            ...prev,
            picture: croppedFile,
            picturePreview: previewUrl
          }));
      
          // Clean up cropper UI
          setShowCropper(false);
          setUpImg(null);
          if (upImg && upImg.startsWith('blob:')) {
            URL.revokeObjectURL(upImg);
          }
        }, 'image/jpeg', 1);
      };

      
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Validate required fields
            if (!formData.name || !formData.name.trim()) {
                setError('Name is required');
                setLoading(false);
                return;
            }
            
            if (!formData.email || !formData.email.trim()) {
                setError('Email is required');
                setLoading(false);
                return;
            }
            
            // Initial member data
            const memberData = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                position: formData.position || '',
                department: formData.department || '',
                bio: formData.bio || '',
            };
            
            // Upload image if provided
            if (formData.picture instanceof File) {
                try {
                    const imagePath = `members/${Date.now()}_${formData.picture.name}`;
                    const imageRef = storageRef(storage, imagePath);
                    
                    // Upload the file
                    const uploadResult = await uploadBytes(imageRef, formData.picture);
                    
                    // Get download URL
                    const downloadURL = await getDownloadURL(uploadResult.ref);
                    
                    // Add URL to member data
                    memberData.picture = downloadURL;
                } catch (uploadError) {
                    setError('Failed to upload image: ' + uploadError.message);
                    setLoading(false);
                    return;
                }
            } else if (selectedMember && selectedMember.picture) {
                // Keep existing picture if editing and no new picture uploaded
                memberData.picture = selectedMember.picture;
            }
                        
            if (isEditing && selectedMember) {
                // Update existing member
                await membersAPI.updateMember(selectedMember.id, memberData);
            } else {
                // Add new member
                await membersAPI.createMember(memberData);
            }

            // Reset form and refresh members list
            setFormData({
                name: '',
                email: '',
                position: '',
                department: '',
                bio: '',
                picture: null,
                picturePreview: null
            });
            setIsEditing(false);
            setSelectedMember(null);
            await fetchData();
            
        } catch (err) {
            setError('Failed to save member: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (member) => {
        setIsEditing(true);
        setSelectedMember(member);
        setFormData({
            ...member,
            picture: member.picture // Store URL for existing image
        });
    };

    const handleDelete = async (member) => {
        if (!window.confirm('Are you sure you want to delete this member?')) {
            return;
        }

        try {
            setLoading(true);
            await membersAPI.deleteMember(member.id);
            await fetchData();
        } catch (err) {
            setError('Failed to delete member: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Add search filter function
    const filteredMembers = (members || [])
        .filter(member =>
            member && member.name && member.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => a.name.localeCompare(b.name));

    // Clean up preview URL when component unmounts
    useEffect(() => {
        return () => {
            if (formData.picturePreview) {
                URL.revokeObjectURL(formData.picturePreview);
            }
        };
    }, [formData.picturePreview]);

    const handleGroupAssignment = async (memberId) => {
        try {
            setLoading(true);
            
            const response = await membersAPI.getMemberGroups(memberId);
            
            setSelectedMemberId(memberId);
            setShowGroupModal(true);
            
            // Pass the current groups to the modal
            const currentGroups = response.map(group => group.id);
            setSelectedGroups(currentGroups);
        } catch (err) {
            setError('Failed to fetch member groups');
        } finally {
            setLoading(false);
        }
    };

    const handleGroupSelection = async (selectedGroups) => {
        try {
            setLoading(true);
            
            const response = await membersAPI.updateMemberGroups(selectedMemberId, selectedGroups);
            
            setShowGroupModal(false);
            
            // Refresh data to update the UI
            await fetchData();
        } catch (err) {
            setError('Failed to update member groups');
        } finally {
            setLoading(false);
        }
    };

    // Update the MemberGroups component to use the cached data
    const MemberGroups = ({ member, onManageGroups }) => {
        // Get the groups for this member from the memberGroups object
        const groups = memberGroups[member.id] || [];
                
        return (
            <div className="member-groups">
                <div className="groups-list">
                    {groups.length > 0 ? (
                        groups.map(group => (
                            <span key={group.id} className="group-tag">
                                {group.name}
                            </span>
                        ))
                    ) : (
                        <span className="admin-no-groups">No groups</span>
                    )}
                </div>
                <button onClick={onManageGroups} className="manage-groups-btn">
                    Manage groups
                </button>
            </div>
        );
    };

    const MemberRow = ({ index, style }) => {
        const member = filteredMembers[index];
        if (!member) return null; // Return null if member is undefined
        
        // Default isActive to false if it doesn't exist
        const isActive = member.isActive !== undefined ? member.isActive : false;
        
        return (
            <div style={{
                ...style,
                paddingLeft: '0.75rem',
                paddingRight: '0.75rem',
                paddingTop: '0.5rem',
                paddingBottom: '0.5rem'
            }}>
                <div className="admin-member-item">
                    <div className="member-image-cell">
                        <img 
                            src={member.picture || ''} 
                            alt={member.name || 'Member'} 
                            className="member-thumbnail"
                        />
                    </div>
                    <div className="admin-member-info">
                        {member.name || 'Unnamed Member'}
                    </div>
                    <div className="admin-member-info">
                        {member.role || 'No Role'}
                    </div>
                    <div className="member-status-cell">
                        <span className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
                            {isActive ? 'Active' : 'Inactive'}
                        </span>
                        <button 
                            className={`status-toggle ${isActive ? 'deactivate' : 'activate'}`}
                            onClick={() => handleStatusToggle(member)}
                        >
                            {isActive ? 'Deactivate' : 'Activate'}
                        </button>
                    </div>
                    <div className="admin-member-groups">
                        <MemberGroups 
                            member={member}
                            onManageGroups={() => handleGroupAssignment(member.id)}
                        />
                    </div>
                    <div className="member-actions-cell">
                        <button className="members-edit-button" onClick={() => handleEdit(member)}>Edit</button>
                        <button className="members-delete-button" onClick={() => handleDelete(member)}>Delete</button>
                    </div>
                </div>
            </div>
        );
    };

    const handleStatusToggle = async (member) => {
        try {
            setLoading(true);
            
            // Check if isActive exists, default to false if it doesn't
            const currentStatus = member.isActive !== undefined ? member.isActive : false;
            const newStatus = !currentStatus;
            
            // Create a simple object with just the isActive flag
            const updateData = { isActive: newStatus };
            
            // Update the member with just the status change
            const response = await membersAPI.updateMember(member.id, updateData);
            
            // Refresh the members list to show the updated status
            await fetchData();
        } catch (err) {
            setError('Failed to update member status: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Validate file size (5MB limit)
        if (file.size > 5242880) {
            setError('Image size should be less than 5MB');
            return;
        }
        
        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
            setError('Only JPEG and PNG images are allowed');
            return;
        }
        
        // Clear any previous errors
        setError(null);
        
        const imageUrl = URL.createObjectURL(file);
        
        setFormData(prev => ({
            ...prev,
            picture: file,
            picturePreview: imageUrl
        }));
    };

    if (loading) {
        return <div className="admin-members-container">Loading...</div>;
    }

    // Calculate statistics with null checks
    const totalMembers = (members || []).length;
    const activeMembers = (members || []).filter(member => member && member.isActive !== undefined ? member.isActive : false).length;
    const inactiveMembers = totalMembers - activeMembers;

    return (
        <div className="admin-members-container">
            {showCropper && upImg && (
                <div className="cropper-modal">
                    <div className="cropper-container">
                        <h3>Crop Image</h3>
                        <ReactCrop
                            crop={crop}
                            onChange={(c) => setCrop(c)}
                            onComplete={(c) => {
                            }}
                            aspect={1}
                        >
                            <img
                                src={upImg}
                                onLoad={(e) => onImageLoad(e.target)}
                                alt="Crop me"
                            />
                        </ReactCrop>
                        <div className="cropper-actions">
                            <button onClick={handleCropComplete}>Confirm</button>
                            <button onClick={() => {
                                setShowCropper(false);
                                setUpImg(null);
                                if (upImg && upImg.startsWith('blob:')) {
                                    URL.revokeObjectURL(upImg);
                                }
                            }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
            
            {!isEditing ? (
                <>
                    <div className="header-actions">
                        <h2>Members</h2>
                        <div className="search-container">
                            <input
                                type="text"
                                placeholder="Search members..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                        </div>
                        <button 
                            className="add-button"
                            onClick={() => {
                                setFormData({
                                    name: '',
                                    email: '',
                                    position: '',
                                    department: '',
                                    bio: '',
                                    picture: null,
                                    picturePreview: null
                                });
                                setIsEditing(true);
                                setSelectedMember(null);
                            }}
                        >
                            + Add New Member
                        </button>
                    </div>

                    <div className="members-statistics">
                        <div className="stat-card">
                            <div className="stat-value">{totalMembers}</div>
                            <div className="stat-label">Total Members</div>
                        </div>
                        <div className="stat-card active">
                            <div className="stat-value">{activeMembers}</div>
                            <div className="stat-label">Active Members</div>
                        </div>
                        <div className="stat-card inactive">
                            <div className="stat-value">{inactiveMembers}</div>
                            <div className="stat-label">Inactive Members</div>
                        </div>
                    </div>

                    <div className="members-list">
                        <div className="members-list-header">
                            <div>Photo</div>
                            <div>Name</div>
                            <div>Role</div>
                            <div>Status</div>
                            <div>Groups</div>
                            <div>Actions</div>
                        </div>
                        <div className="members-items-container" style={{ height: 'calc(100vh - 250px)' }}>
                            <AutoSizer>
                                {({ height, width }) => (
                                    <List
                                        height={height}
                                        itemCount={filteredMembers.length}
                                        itemSize={100}
                                        width={width}
                                        overscanCount={5}
                                    >
                                        {MemberRow}
                                    </List>
                                )}
                            </AutoSizer>
                        </div>
                    </div>
                </>
            ) : (
                <div className="edit-view">
                    <div className="edit-header">
                        <button 
                            className="back-button"
                            onClick={() => {
                                setIsEditing(false);
                                setSelectedMember(null);
                                setFormData({
                                    name: '',
                                    email: '',
                                    position: '',
                                    department: '',
                                    bio: '',
                                    picture: null,
                                    picturePreview: null
                                });
                            }}
                        >
                            ← Back to Members
                        </button>
                        <h2>{selectedMember ? `Edit ${selectedMember.name}` : 'Add New Member'}</h2>
                    </div>

                    {error && <div className="error-message">{error}</div>}
                    
                    <form onSubmit={handleSubmit} className="member-form">
                        <div className="form-group">
                            <label>Name:</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Email:</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Position:</label>
                            <input
                                type="text"
                                name="position"
                                value={formData.position}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Department:</label>
                            <input
                                type="text"
                                name="department"
                                value={formData.department}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Bio:</label>
                            <textarea
                                name="bio"
                                value={formData.bio}
                                onChange={handleInputChange}
                                rows="4"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label className="file-input-label">Member Photo:</label>
                            <div className="member-upload-container">
                                {formData.picturePreview ? (
                                    <div className="member-preview-wrapper">
                                        <div className="member-preview-container">
                                            <img 
                                                src={formData.picturePreview} 
                                                alt="Member preview" 
                                                className="member-preview-image"
                                            />
                                            <div className="member-image-overlay">
                                                <label htmlFor="member-image-upload" className="member-change-image-btn">
                                                    <FaCamera style={{ marginRight: '5px' }} />
                                                    Change Photo
                                                </label>
                                            </div>
                                        </div>
                                        <button 
                                            type="button" 
                                            className="remove-image-btn" 
                                            onClick={() => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    picture: null,
                                                    picturePreview: null
                                                }));
                                            }}
                                            style={{ width: '100%', marginTop: '10px' }}
                                        >
                                            Remove Photo
                                        </button>
                                    </div>
                                ) : (
                                    <label htmlFor="member-image-upload" className="member-upload-placeholder">
                                        <div className="member-upload-btn">
                                            <FaUpload size={24} />
                                            <span>Upload Photo</span>
                                        </div>
                                        <p className="member-upload-hint">Click to select or drag and drop</p>
                                        <p className="member-upload-formats">JPEG, PNG (Max 5MB)</p>
                                    </label>
                                )}
                                <input
                                    type="file"
                                    id="member-image-upload"
                                    name="member-image"
                                    accept="image/jpeg, image/png"
                                    onChange={handleImageUpload}
                                    className="member-file-input"
                                    tabIndex="-1"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="submit" disabled={loading}>
                                {selectedMember ? 'Save Changes' : 'Add Member'}
                            </button>
                            <button 
                                type="button" 
                                className="cancel-button"
                                onClick={() => {
                                    setIsEditing(false);
                                    setSelectedMember(null);
                                    setFormData({
                                        name: '',
                                        email: '',
                                        position: '',
                                        department: '',
                                        bio: '',
                                        picture: null,
                                        picturePreview: null
                                    });
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}
            
            {showGroupModal && (
                <GroupSelectionModal
                    memberId={selectedMemberId}
                    onSave={handleGroupSelection}
                    onClose={() => setShowGroupModal(false)}
                    selectedGroups={selectedGroups}
                />
            )}
        </div>
    );
}

export default AdminMembers; 